import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { PageHero } from '../components/PageHero';
import { 
    Trophy, 
    Medal, 
    TrendingUp, 
    Flame,
    Crown,
    Target,
    Percent,
    Coins
} from 'lucide-react';
import { formatAddress } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LeaderboardPage() {
    const { t } = useTranslation();
    const { address } = useWeb3();
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [timeframe, setTimeframe] = useState('all');

    useEffect(() => {
        fetchLeaderboard();
    }, [timeframe]);

    const fetchLeaderboard = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/leaderboard?timeframe=${timeframe}`);
            const data = await response.json();
            setLeaderboard(data);
        } catch (error) {
            console.error('Failed to fetch leaderboard:', error);
            // Mock data for demo
            setLeaderboard([
                { rank: 1, address: '0x742d35Cc6634C0532925a3b844Bc454e4438f44E', wins: 15, losses: 3, profit: 2.45, winRate: 83.3, totalBets: 18 },
                { rank: 2, address: '0x8ba1f109551bD432803012645Ac136ddd64DBA72', wins: 12, losses: 4, profit: 1.89, winRate: 75.0, totalBets: 16 },
                { rank: 3, address: '0xdD2FD4581271e230360230F9337D5c0430Bf44C0', wins: 10, losses: 5, profit: 1.23, winRate: 66.7, totalBets: 15 },
                { rank: 4, address: '0x1234567890AbcdEF1234567890AbcdEF12345678', wins: 8, losses: 4, profit: 0.95, winRate: 66.7, totalBets: 12 },
                { rank: 5, address: '0xAf122740bD739B49d980a48Dfb6E0141cCE79338', wins: 7, losses: 3, profit: 0.78, winRate: 70.0, totalBets: 10 },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const getRankIcon = (rank) => {
        switch (rank) {
            case 1:
                return <Crown className="w-6 h-6 text-[#FFD700]" />;
            case 2:
                return <Medal className="w-6 h-6 text-gray-400" />;
            case 3:
                return <Medal className="w-6 h-6 text-amber-600" />;
            default:
                return <span className="w-6 h-6 flex items-center justify-center text-white/50 font-bold">{rank}</span>;
        }
    };

    const getRankBg = (rank) => {
        switch (rank) {
            case 1:
                return 'bg-gradient-to-r from-[#FFD700]/20 to-[#FFD700]/5 border-[#FFD700]/30';
            case 2:
                return 'bg-gradient-to-r from-gray-500/20 to-gray-500/5 border-gray-500/30';
            case 3:
                return 'bg-gradient-to-r from-amber-600/20 to-amber-600/5 border-amber-600/30';
            default:
                return 'bg-white/[0.02] border-white/5';
        }
    };

    const isCurrentUser = (addr) => address?.toLowerCase() === addr?.toLowerCase();

    return (
        <div className="page-enter" data-testid="leaderboard-page">
            <PageHero
                icon={Trophy}
                title={t('leaderboard.title')}
                highlight={t('leaderboard.highlight')}
                subtitle={t('leaderboard.subtitle')}
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

            {/* Timeframe Tabs */}
            <Tabs value={timeframe} onValueChange={setTimeframe} className="mb-6">
                <TabsList className="bg-white/5 border border-white/10">
                    <TabsTrigger value="all" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-[#0A0A0C]">
                        {t('leaderboard.allTime')}
                    </TabsTrigger>
                    <TabsTrigger value="month" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-[#0A0A0C]">
                        {t('leaderboard.thisMonth')}
                    </TabsTrigger>
                    <TabsTrigger value="week" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-[#0A0A0C]">
                        {t('leaderboard.thisWeek')}
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up delay-1">
                    <CardContent className="py-4">
                        <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
                            <Target className="w-4 h-4" />
                            {t('leaderboard.totalPlayers')}
                        </div>
                        <div className="text-2xl font-bold text-white stat-number">{leaderboard.length}</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up delay-2">
                    <CardContent className="py-4">
                        <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
                            <Flame className="w-4 h-4 text-orange-500" />
                            {t('leaderboard.highestWinRate')}
                        </div>
                        <div className="text-2xl font-bold text-[#00FF66] stat-number">
                            {leaderboard[0]?.winRate?.toFixed(1) || 0}%
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up delay-3">
                    <CardContent className="py-4">
                        <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
                            <Coins className="w-4 h-4 text-[#FFD700]" />
                            {t('leaderboard.topProfit')}
                        </div>
                        <div className="text-2xl font-bold text-[#FFD700]">
                            {leaderboard[0]?.profit?.toFixed(2) || 0} ETH
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up delay-4">
                    <CardContent className="py-4">
                        <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
                            <TrendingUp className="w-4 h-4 text-[#00FF66]" />
                            {t('leaderboard.mostWins')}
                        </div>
                        <div className="text-2xl font-bold text-white stat-number">
                            {leaderboard[0]?.wins || 0}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Leaderboard Table */}
            <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up delay-5">
                <CardHeader>
                    <CardTitle className="text-xl text-white">{t('leaderboard.rankings')}</CardTitle>
                    <CardDescription className="text-white/50">
                        {t('leaderboard.rankingsDesc')}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="space-y-4">
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} className="h-16 w-full bg-white/5" />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {leaderboard.map((player) => (
                                <div
                                    key={player.address}
                                    className={`flex items-center justify-between p-4 rounded-xl border transition-all hover:scale-[1.01] ${getRankBg(player.rank)} ${isCurrentUser(player.address) ? 'ring-2 ring-[#00FF66]' : ''}`}
                                >
                                    <div className="flex items-center gap-4">
                                        {/* Rank */}
                                        <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                                            {getRankIcon(player.rank)}
                                        </div>
                                        
                                        {/* Address */}
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-white">
                                                    {formatAddress(player.address)}
                                                </span>
                                                {isCurrentUser(player.address) && (
                                                    <Badge className="bg-[#00FF66]/20 text-[#00FF66] text-xs">{t('common.you')}</Badge>
                                                )}
                                                {player.rank === 1 && (
                                                    <Badge className="bg-[#FFD700]/20 text-[#FFD700] text-xs">
                                                        <Crown className="w-3 h-3 mr-1" />
                                                        {t('leaderboard.champion')}
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="text-sm text-white/40">
                                                {t('leaderboard.betsCount', { count: player.totalBets })}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Stats */}
                                    <div className="flex items-center gap-6">
                                        <div className="text-center">
                                            <div className="text-xs text-white/40">W/L</div>
                                            <div className="font-semibold text-white">
                                                <span className="text-[#00FF66]">{player.wins}</span>
                                                <span className="text-white/30">/</span>
                                                <span className="text-red-400">{player.losses}</span>
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xs text-white/40">{t('leaderboard.winRate')}</div>
                                            <div className="font-semibold text-[#00FF66]">
                                                {player.winRate.toFixed(1)}%
                                            </div>
                                        </div>
                                        <div className="text-center min-w-[80px]">
                                            <div className="text-xs text-white/40">{t('leaderboard.profit')}</div>
                                            <div className={`font-mono font-bold ${player.profit >= 0 ? 'text-[#00FF66]' : 'text-red-400'}`}>
                                                {player.profit >= 0 ? '+' : ''}{player.profit.toFixed(2)} ETH
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Your Position */}
            {address && !leaderboard.some(p => isCurrentUser(p.address)) && (
                <Card className="mt-6 bg-white/[0.02] border-[#00FF66]/20">
                    <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#00FF66]/20 flex items-center justify-center">
                                    <Target className="w-5 h-5 text-[#00FF66]" />
                                </div>
                                <div>
                                    <div className="text-white font-semibold">{t('leaderboard.notRankedYet')}</div>
                                    <div className="text-sm text-white/50">{t('leaderboard.winSomeToAppear')}</div>
                                </div>
                            </div>
                            <Link 
                                to="/create"
                                className="px-4 py-2 bg-[#00FF66] text-[#0A0A0C] rounded-lg font-semibold hover:bg-[#00CC52] transition-colors"
                            >
                                {t('leaderboard.createBetCta')}
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            )}
            </div>
        </div>
    );
}
